// The campus's preset room names, for the Eng Tour Creator.
//
// A tour editor knows "Engineering Library" and "Makerspace", not E3-362, and a
// stop called "E2-442" says nothing on a visitor's screen. The names already
// exist — the authored RoomNames/<slug>.csv sheets plus the operator's
// published overlay (/api/room-names), the pair the pathfinder and the Event
// Creator read — so this module reads the SAME pair for the five EITC-area
// buildings rather than letting the creator keep a list of its own: one name
// on every screen, and a name the operator publishes reaches the tour editor
// with no deploy. Pure (fetch is passed in or taken from the global), so
// `node src/utils/engtourRoomNames.test.mjs` runs it against the shipped sheets.

import { parseRoomSheet, isShaftRoom, naturalCompare } from './roomSheets.js';
import { applyRoomNameOverlay } from './roomNameOverlay.js';

/** Sheet slug ↔ the building key the creator stores ↔ the code it prints. */
export const ENGTOUR_BUILDINGS = Object.freeze([
  { slug: 'eitc_e1', building: 'e1', code: 'E1' },
  { slug: 'eitc_e2', building: 'e2', code: 'E2' },
  { slug: 'eitc_e3', building: 'e3', code: 'E3' },
  { slug: 'stanley_pauley_centre', building: 'stanley_pauley_centre', code: 'SP-C' },
  { slug: 'stanley_pauley_engineering', building: 'stanley_pauley_engineering', code: 'SP' },
]);

/** creator building → short code, e.g. `e3` → `E3`. */
export const SHORT_CODES = Object.freeze(Object.fromEntries(ENGTOUR_BUILDINGS.map((b) => [b.building, b.code])));

const BUILDING_BY_SLUG = new Map(ENGTOUR_BUILDINGS.map((b) => [b.slug, b.building]));
const BUILDING_BY_CODE = new Map(ENGTOUR_BUILDINGS.map((b) => [b.code.toLowerCase(), b.building]));

export const SHEETS_PATH = '/Map%20Info/pathfinder/RoomNames/';

/**
 * The creator's `normalizeBuilding` rule — lower-case, strip a leading `eitc`
 * and its separator — so 'EITC E2', 'eitc_e2' and 'E2' are all `e2`. A short
 * code ('SP-C', 'sp') is taken too, since that is how the creator prints one.
 */
export function normalizeBuilding(b) {
  if (b == null) return '';
  const n = String(b).toLowerCase().replace(/^eitc[\s_-]*/, '').trim();
  return BUILDING_BY_CODE.get(n) || n;
}

/** 'E3-362' — the building alone when there is no room, never 'E3-null'. */
export function roomCodeFor(building, room) {
  const code = SHORT_CODES[normalizeBuilding(building)] || String(building || '').toUpperCase();
  const r = room == null ? '' : String(room).trim();
  return r ? `${code}-${r}` : code;
}

/** `fetchRoomNameOverlay` with the fetch passed in; same shape check, same silence on failure. */
async function fetchOverlay(apiBase, fetchImpl) {
  try {
    const r = await fetchImpl(`${apiBase}/api/room-names`);
    if (!r?.ok) return null;
    const names = (await r.json())?.names;
    return names && typeof names === 'object' && !Array.isArray(names) ? names : null;
  } catch {
    return null;
  }
}

async function fetchSheet(apiBase, slug, fetchImpl) {
  try {
    const r = await fetchImpl(`${apiBase}${SHEETS_PATH}${encodeURIComponent(slug)}.csv`);
    if (!r?.ok) return [];
    return parseRoomSheet(await r.text())
      .filter((row) => row.roomName) // the pathfinder's rule: a blank name is nothing to show
      .map((row) => ({ building: slug, roomNumber: row.roomNumber, roomName: row.roomName }));
  } catch {
    return [];
  }
}

/** Overlay-merged sheet rows → the `RoomNames` object the creator reads. */
export function buildRoomNames(rows) {
  const byBuilding = Object.fromEntries(ENGTOUR_BUILDINGS.map((b) => [b.building, {}]));
  const named = [];
  for (const r of rows || []) {
    const building = BUILDING_BY_SLUG.get(r?.building);
    const room = String(r?.roomNumber ?? '').trim();
    const name = String(r?.roomName ?? '').trim();
    if (!building || !room || !name || isShaftRoom(room)) continue;
    const key = room.toLowerCase();
    if (byBuilding[building][key]) continue; // first spelling wins, as the sheets do
    byBuilding[building][key] = name;
    named.push({ building, room, name, code: roomCodeFor(building, room) });
  }
  const order = new Map(ENGTOUR_BUILDINGS.map((b, i) => [b.building, i]));
  named.sort((a, b) => (order.get(a.building) - order.get(b.building)) || naturalCompare(a.room, b.room));
  return { byBuilding, named };
}

/**
 * The five sheets and the live overlay, fetched together. A sheet that fails
 * contributes nothing and an overlay that fails costs only the published names
 * — this never throws for one building.
 */
// `apiBase` is where /api/room-names lives; `sheetsBase` is where the static
// sheets live. They are the same origin in production and DIFFERENT ports in
// dev (CRA serves public/ on :3000, the Express API is :3001), which is why the
// sheets default to the page's own origin rather than following apiBase.
export async function loadEngTourRoomNames({ apiBase = '', sheetsBase = '', fetchImpl } = {}) {
  const f = fetchImpl || ((...args) => fetch(...args)); // the page's own fetch unless a test hands one in
  const [overlay, ...sheets] = await Promise.all([
    fetchOverlay(apiBase, f),
    ...ENGTOUR_BUILDINGS.map((b) => fetchSheet(sheetsBase, b.slug, f)),
  ]);
  const rows = sheets.flat();
  return buildRoomNames(applyRoomNameOverlay(rows, overlay) || rows);
}

/** The preset name for a room, or '' — any building spelling the creator uses. */
export function roomNameFor(names, building, room) {
  const b = names?.byBuilding?.[normalizeBuilding(building)];
  if (!b || room == null) return '';
  return b[String(room).trim().toLowerCase()] || '';
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Named rooms whose name or code contains every token of the query. A token at
 * the start of a word ("lib" → "Library") outranks one inside a word
 * ("gap" → "ENGAP"); ties go to the shorter name, then sheet order.
 */
export function searchRoomNames(names, query, limit = 12) {
  const tokens = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length || !names?.named?.length) return [];
  const scored = [];
  for (let i = 0; i < names.named.length; i++) {
    const row = names.named[i];
    const hay = `${row.name} ${row.code} ${row.code.replace(/-/g, ' ')}`.toLowerCase();
    let score = 0;
    for (const t of tokens) {
      if (new RegExp(`(^|[^a-z0-9])${escapeRe(t)}`).test(hay)) score += 2;
      else if (hay.includes(t)) score += 1;
      else { score = -1; break; }
    }
    if (score >= 0) scored.push({ row, score, i });
  }
  scored.sort((a, b) => b.score - a.score || a.row.name.length - b.row.name.length || a.i - b.i);
  return scored.slice(0, limit).map((s) => s.row);
}
